import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Circle, useMap, useMapEvents } from 'react-leaflet';
import '../styles/MapComponent.css';
import 'leaflet/dist/leaflet.css';
import SpeciesMarker from './SpeciesMarker';

function MapUpdater({ center, zoom }) {
  const map = useMap();
  const prevCenterRef = useRef(null);
  const prevZoomRef = useRef(null);

  // Only re-center when the search location actually changes (not on every render)
  useEffect(() => {
    if (!center) return;
    const [lat, lng] = center;
    const prev = prevCenterRef.current;
    if (!prev || prev[0] !== lat || prev[1] !== lng || prevZoomRef.current !== zoom) {
      prevCenterRef.current = [lat, lng];
      prevZoomRef.current = zoom;
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);

  return null;
}

// Listens for clicks on the map background (not on markers) to deselect
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      // originalEvent.target is the map container itself for background clicks;
      // marker clicks are handled separately and stop propagation won't work
      // reliably, so we use a small delay to let marker clicks set a flag first.
      if (onMapClick) onMapClick();
    }
  });
  return null;
}

function MapComponent({
  center,
  zoom = 10,
  showSpecies = false,
  speciesData = [],
  selectedRadius = 5,
  searchLocation = null,
  onSpeciesClick = null,
  selectedSpeciesId = null
}) {
  const [mapType, setMapType] = useState('satellite');
  const [hoveredButton, setHoveredButton] = useState(false);
  const markerClickedRef = useRef(false);

  const toggleMapType = () => {
    setMapType(mapType === 'satellite' ? 'street' : 'satellite');
  };

  // Toggle selection on marker click
  const handleMarkerClick = useCallback((species) => {
    markerClickedRef.current = true;
    if (selectedSpeciesId === species.id) {
      if (onSpeciesClick) onSpeciesClick(null);
    } else {
      if (onSpeciesClick) onSpeciesClick(species);
    }
  }, [selectedSpeciesId, onSpeciesClick]);

  // Clicking the map background deselects
  const handleMapClick = useCallback(() => {
    if (markerClickedRef.current) {
      markerClickedRef.current = false;
      return;
    }
    if (selectedSpeciesId) {
      if (onSpeciesClick) onSpeciesClick(null);
    }
  }, [selectedSpeciesId, onSpeciesClick]);

  // Convert miles to meters for the circle radius (1 mile = 1609.34 meters)
  const radiusInMeters = selectedRadius * 1609.34;

  // Tile layer URLs
  const satelliteUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
  const streetUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

  return (
    <div className="map-wrapper">
      <MapContainer
        center={center}
        zoom={zoom}
        className="map-container"
        zoomControl={true}
      >
        <MapUpdater center={center} zoom={zoom} />
        <MapClickHandler onMapClick={handleMapClick} />

        <TileLayer
          attribution={mapType === 'satellite'
            ? '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'}
          url={mapType === 'satellite' ? satelliteUrl : streetUrl}
        />

        {searchLocation && (
          <Circle
            center={[searchLocation.lat, searchLocation.lng]}
            radius={radiusInMeters}
            pathOptions={{
              color: '#000',
              fillColor: 'transparent',
              fillOpacity: 0,
              weight: 3
            }}
          />
        )}

        {showSpecies && speciesData.flatMap((species) =>
          (species.locations || [species.location]).map((loc, i) => (
            <SpeciesMarker
              key={`${species.id}-${i}`}
              species={{ ...species, location: loc, lat: loc[0], lng: loc[1] }}
              onClick={handleMarkerClick}
              isSelected={selectedSpeciesId === species.id}
            />
          ))
        )}
      </MapContainer>

      <button
        className={`map-type-toggle ${hoveredButton ? 'hovered' : ''}`}
        onClick={toggleMapType}
        onMouseEnter={() => setHoveredButton(true)}
        onMouseLeave={() => setHoveredButton(false)}
        title={`Switch to ${mapType === 'satellite' ? 'street' : 'satellite'} view`}
      >
        {mapType === 'satellite' ? (
          <div className="map-preview street-preview">
            <div className="road-line"></div>
            <div className="road-line"></div>
          </div>
        ) : (
          <div className="map-preview satellite-preview">
            <div className="terrain-pattern"></div>
          </div>
        )}
      </button>
    </div>
  );
}

export default MapComponent;
